import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, ErrorState, Spinner, cx, FIELD_CLASS } from '@/components/ui';
import { formatDateTime } from '@/lib/format';
import { ApiException } from '@/lib/api/types';
import {
  fetchNotificationSettings,
  removeNotificationDevice,
  setSlackNotification,
  updateNotificationDevice,
  type NotificationDevice,
  type NotificationSettings,
} from '@/lib/push';

/**
 * 알림 받는 곳. (2026-09-03 사용자 요청, V57)
 *
 * "이거 PC랑 모바일 각각 나눠서 설정할 수 있어? ... pc, slack, 휴대폰 모두
 *  나눠서 설정이 가능한가?"
 *
 * 🔴 전에는 나눌 수가 없었다
 *   발송이 회원의 전 구독으로 나갔고, 끄는 방법은 그 브라우저에 직접 가서
 *   끄는 것뿐이었다. 회사 PC 에 켜둔 알림을 집에서 끌 방법이 없었다.
 *
 * 🔴 끄기와 지우기가 다르다
 *   끄기 - 구독은 남기고 발송만 건너뛴다. 다시 켤 때 권한 팝업이 안 뜬다
 *   지우기 - 구독을 끊는다. 다시 받으려면 그 브라우저에서 새로 켜야 한다
 *   잃어버린 폰이나 반납한 PC 는 지우기다.
 *
 * 알림을 켜고 끄는 것(이 브라우저의 구독 자체)은 위의 PushSettings 카드가 한다.
 * 여기는 이미 켜둔 곳들을 관리하는 자리다.
 */
export function NotificationChannels() {
  const queryClient = useQueryClient();

  const settings = useQuery<NotificationSettings, ApiException>({
    queryKey: ['push', 'settings'],
    queryFn: fetchNotificationSettings,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['push', 'settings'] });
  };

  const updateDevice = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: { enabled?: boolean; label?: string } }) =>
      updateNotificationDevice(id, patch),
    onSuccess: invalidate,
  });

  const removeDevice = useMutation({
    mutationFn: (id: number) => removeNotificationDevice(id),
    onSuccess: invalidate,
  });

  const slack = useMutation({
    mutationFn: (enabled: boolean) => setSlackNotification(enabled),
    onSuccess: invalidate,
  });

  if (settings.isLoading) {
    return (
      <Card title="알림 받는 곳">
        <Spinner label="설정을 불러오는 중" />
      </Card>
    );
  }

  if (settings.error || !settings.data) {
    return (
      <Card title="알림 받는 곳">
        <ErrorState
          message={settings.error?.message ?? '설정을 불러오지 못했습니다.'}
          onRetry={() => void settings.refetch()}
        />
      </Card>
    );
  }

  const { devices, slackEnabled, slackAllowed } = settings.data;
  const activeCount = devices.filter((device) => device.enabled).length;

  return (
    <Card title="알림 받는 곳">
      <p className="text-xs text-fg-muted">
        가격 알림 같은 개인 알림이 어디로 갈지 정합니다. 켜둔 곳 모두에 동시에 갑니다.
      </p>

      <ul className="mt-3 flex flex-col gap-1">
        {devices.map((device) => (
          <DeviceRow
            key={device.id}
            device={device}
            busy={updateDevice.isPending || removeDevice.isPending}
            onToggle={(enabled) => updateDevice.mutate({ id: device.id, patch: { enabled } })}
            onRename={(label) => updateDevice.mutate({ id: device.id, patch: { label } })}
            onRemove={() => removeDevice.mutate(device.id)}
          />
        ))}

        {/*
          Slack 은 기기가 아니라 채널이라 목록 맨 아래에 따로 둔다.
          🔴 2026-09-28 사용자 지시 - Slack 개인 알림은 관리자 전용 채널(02-관리자-알림)로만 간다.
            전에는 누가 걸었든 공통 채널에 올라와 다른 회원의 가격 알림이 모두에게 보였다.
            관리자가 아니면 스위치를 보여주지 않는다(서버도 켜기를 거부한다 - PushSubscriptionService.setSlackEnabled).
        */}
        <li className="flex items-center justify-between gap-2 rounded-lg px-2 py-2 hover:bg-surface-muted">
          <span className="flex min-w-0 items-center gap-2">
            <span aria-hidden="true">💬</span>
            <span className="truncate text-sm text-fg">
              {slackAllowed ? 'Slack 관리자 알림 채널' : 'Slack'}
            </span>
          </span>
          {slackAllowed ? (
            <Toggle
              on={slackEnabled}
              busy={slack.isPending}
              onChange={(next) => slack.mutate(next)}
              label="Slack 알림"
            />
          ) : (
            <span className="shrink-0 text-xs text-fg-subtle">관리자만 받습니다</span>
          )}
        </li>
      </ul>

      {devices.length === 0 && (
        <p className="mt-2 text-xs text-fg-subtle">
          아직 알림을 켠 기기가 없습니다. 위 <strong>폰 알림</strong>에서 이 기기의 알림을
          켜면 목록에 들어옵니다.
        </p>
      )}

      <div className="mt-3 space-y-1 text-xs text-fg-subtle">
        <p>
          <strong>끄기</strong>는 구독을 남긴 채 발송만 건너뜁니다 — 다시 켤 때 권한을
          새로 묻지 않습니다. <strong>지우기</strong>는 연결 자체를 끊어서, 다시 받으려면
          그 기기에서 알림을 새로 켜야 합니다. 잃어버린 폰이나 반납한 PC를 정리할 때 씁니다.
        </p>
        <p>
          PC 알림은 <strong>브라우저가 실행 중이면</strong> 다른 페이지를 보고 있어도,
          이 사이트 탭이 닫혀 있어도 뜹니다. 브라우저를 완전히 종료하면 오지 않습니다.
          폰은 앱을 닫아도 옵니다.
        </p>
        {activeCount === 0 && devices.length > 0 && (
          <p className="text-warn">
            {/* ✏️ 9/28 — "Slack 으로만 알림이 가지 않습니다" 로 조립돼 반대 뜻으로 읽혔다. 두 문장으로 가른다 */}
            켜둔 기기가 하나도 없습니다.{' '}
            {slackEnabled ? '지금은 Slack 으로만 알림이 갑니다.' : '지금은 어디로도 알림이 가지 않습니다.'}
          </p>
        )}
      </div>
    </Card>
  );
}

/**
 * 기기 한 줄.
 *
 * 이름을 고칠 수 있게 둔 이유 — 서버의 추정이 "윈도우 PC (크롬)" 까지만 알아본다.
 * PC가 둘이면 구분이 안 되므로 "집 PC" 처럼 직접 붙일 수 있어야 한다.
 */
function DeviceRow({
  device,
  busy,
  onToggle,
  onRename,
  onRemove,
}: {
  device: NotificationDevice;
  busy: boolean;
  onToggle: (enabled: boolean) => void;
  onRename: (label: string) => void;
  onRemove: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(device.label ?? '');

  const name = device.label ?? '이름 없는 기기';
  // 폰과 PC를 눈으로 가른다. 이름이 없으면 기본 아이콘을 쓴다
  const icon = /아이폰|아이패드|안드로이드/.test(name) ? '📱' : '💻';

  if (editing) {
    return (
      <li className="flex items-center gap-2 rounded-lg px-2 py-2">
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          maxLength={60}
          placeholder="집 PC"
          aria-label="기기 이름"
          className={cx(FIELD_CLASS, 'min-w-0 flex-1')}
        />
        <Button
          variant="secondary"
          disabled={busy}
          onClick={() => {
            onRename(draft.trim());
            setEditing(false);
          }}
        >
          저장
        </Button>
        <Button variant="ghost" onClick={() => setEditing(false)}>
          취소
        </Button>
      </li>
    );
  }

  return (
    <li
      className={cx(
        'flex items-center justify-between gap-2 rounded-lg px-2 py-2 hover:bg-surface-muted',
        !device.enabled && 'opacity-60',
      )}
    >
      <span className="flex min-w-0 flex-1 items-center gap-2">
        <span aria-hidden="true">{icon}</span>
        <span className="min-w-0">
          <span className="flex items-center gap-1.5">
            <span className="truncate text-sm text-fg">{name}</span>
            {device.current && <Badge tone="info">지금 이 기기</Badge>}
          </span>
          <span className="block text-xs text-fg-subtle">
            {device.lastSentAt
              ? `마지막 알림 ${formatDateTime(device.lastSentAt)}`
              : '아직 알림이 간 적 없음'}
          </span>
        </span>
      </span>

      <span className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={() => {
            setDraft(device.label ?? '');
            setEditing(true);
          }}
          className="rounded px-1 text-xs text-fg-muted underline hover:text-fg"
        >
          이름
        </button>
        <button
          type="button"
          onClick={onRemove}
          disabled={busy}
          className="rounded px-1 text-xs text-fg-muted underline hover:text-fg"
        >
          지우기
        </button>
        <Toggle
          on={device.enabled}
          busy={busy}
          onChange={onToggle}
          label={`${name} 알림`}
        />
      </span>
    </li>
  );
}

/**
 * 켬/끔 스위치.
 *
 * 🔴 체크박스가 아니라 버튼이다
 *   상태가 글자로도 보여야 한다. 색만으로 켜짐/꺼짐을 나타내면
 *   색을 구분하기 어려운 사람에게는 아무 정보가 없다.
 */
function Toggle({
  on,
  busy,
  onChange,
  label,
}: {
  on: boolean;
  busy: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={busy}
      onClick={() => onChange(!on)}
      className={cx(
        'min-w-[3rem] rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
        'disabled:opacity-50',
        on ? 'bg-brand text-brand-fg' : 'bg-surface-hover text-fg-muted',
      )}
    >
      {on ? '켬' : '끔'}
    </button>
  );
}
